// SpearBI design tokens — single source of truth for colour, type, spacing,
// radii and elevation.
//
// Direction: "quiet chrome, loud data". This is a dense reporting tool, so the
// interface furniture stays near-monochrome and the only saturated colour in a
// view is the data itself (a delta, an exception, the active channel).
//
// Contrast note: the legacy brand teal (#58b6c0) measures 2.37:1 on white and
// fails WCAG AA for text, yet it was used for links, labels and active states
// throughout. `brand.500` below is the same hue darkened to 4.94:1 so it can
// carry text and interactive state. The original tint survives as `brand.300`
// for fills, chart series and decoration, where contrast rules don't apply.

// --- Neutrals -------------------------------------------------------------
// A marine-cast grey ramp rather than a pure grey: it sits under a teal brand
// without the two reading as different temperatures.
export const neutral = {
  0: "#FFFFFF",
  25: "#FAFCFD", // table zebra
  50: "#F6F8FA", // page ground
  100: "#EDF2F5", // subtle fill, hover row
  200: "#E2E9EE", // hairline / divider
  300: "#CBD6DE", // strong border, disabled edge
  400: "#93A5B1", // muted text, placeholder
  500: "#64798A", // secondary text, micro-labels
  600: "#4A5D6B", // body text on light
  700: "#33454F", // strong body
  800: "#1D2F3A", // headings
  900: "#0F1E28", // primary numerals, ink
};

// --- Brand ----------------------------------------------------------------
export const brand = {
  50: "#EFF7F8",
  100: "#DCEEF0",
  200: "#B4DCE0",
  300: "#58B6C0", // legacy tint — fills / charts / decoration only
  400: "#219AA6",
  500: "#0E7C86", // interactive: links, active state, focus (4.94:1 on white)
  600: "#0A5F67", // hover / pressed
  700: "#084B52",
  800: "#063A3F",
  900: "#04272B",
};

// --- Signal ---------------------------------------------------------------
// Used only to encode meaning in data: growth, decline, caution, information.
// Never decorative.
export const signal = {
  up: "#12775F",
  upSoft: "#E4F2ED",
  down: "#BF3F38",
  downSoft: "#FBEAE9",
  warn: "#A8690A",
  warnSoft: "#FBF1E1",
  info: "#2C6E9B",
  infoSoft: "#E8F1F7",
  neutral: neutral[500],
  neutralSoft: neutral[100],
};

// --- Chart series ---------------------------------------------------------
// One ordered, colour-blind-safe ramp so every chart in the product reads as
// the same system. Charts currently mix teal/green/red/orange arbitrarily.
export const series = [
  "#0E7C86",
  "#58B6C0",
  "#2C6E9B",
  "#7FA8C4",
  "#12775F",
  "#63A98C",
  "#A8690A",
  "#D6A75B",
];

// --- Typography -----------------------------------------------------------
export const type = {
  // Inter is already loaded in public/index.html.
  sans: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`,
  mono: `'Inter', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`,

  // A tight, deliberate scale. Nothing between 14 and 20 — the gap is what
  // makes the hierarchy readable at a glance in a dense grid.
  size: {
    micro: "0.656rem", // 10.5px — uppercase eyebrow labels
    caption: "0.75rem", // 12px  — table body, chart axes
    data: "0.781rem", // 12.5px — dense numeric cells
    small: "0.812rem", // 13px  — secondary UI text
    body: "0.875rem", // 14px  — default UI text
    lead: "1rem", // 16px
    title: "1.25rem", // 20px  — page title
    display: "1.875rem", // 30px  — KPI numeral
  },

  weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },

  // Tracking tightens as size grows; uppercase micro-labels open right up.
  tracking: {
    display: "-0.022em",
    title: "-0.015em",
    body: "0",
    label: "0.085em",
  },
};

// --- Shape & elevation ----------------------------------------------------
// Radii come down from the previous 20px pillowed cards. Elevation is a
// hairline plus one soft shadow — no double-shadow neumorphism.
export const radius = { sm: "6px", md: "10px", lg: "14px", pill: "999px" };

export const elevation = {
  flat: "none",
  hairline: `0 0 0 1px ${neutral[200]}`,
  raised: `0 1px 2px rgba(15,30,40,0.04), 0 2px 8px rgba(15,30,40,0.05)`,
  overlay: `0 4px 12px rgba(15,30,40,0.08), 0 12px 32px rgba(15,30,40,0.10)`,
  // Sticky chrome: a shadow only once content has scrolled beneath it.
  sticky: `0 1px 0 ${neutral[200]}, 0 4px 16px rgba(15,30,40,0.05)`,
};

// --- Layout ---------------------------------------------------------------
export const layout = {
  sidebarWidth: "208px",
  sidebarWidthCollapsed: "64px",
  topBarHeight: "52px",
  channelRailHeight: "46px",
  get chromeHeight() {
    return `calc(${this.topBarHeight} + ${this.channelRailHeight})`;
  },
  contentGutter: "20px",
};

// --- Motion ---------------------------------------------------------------
// Short and few. A reporting tool should feel instant; the previous build
// animated colour on every element and counted every number up from zero.
export const motion = {
  fast: "120ms cubic-bezier(0.2, 0, 0.38, 0.9)",
  base: "180ms cubic-bezier(0.2, 0, 0.38, 0.9)",
  slow: "260ms cubic-bezier(0.2, 0, 0.38, 0.9)",
};

// --- Composed style objects ----------------------------------------------
// Reusable `sx` fragments so pages don't re-derive the same recipe.

export const surface = {
  panel: {
    bg: neutral[0],
    borderRadius: radius.md,
    border: "1px solid",
    borderColor: neutral[200],
    boxShadow: elevation.raised,
  },
  quiet: {
    bg: neutral[0],
    borderRadius: radius.md,
    border: "1px solid",
    borderColor: neutral[200],
  },
  inset: {
    bg: neutral[50],
    borderRadius: radius.sm,
    border: "1px solid",
    borderColor: neutral[200],
  },
};

// Uppercase eyebrow used above every number and section.
export const eyebrow = {
  fontSize: type.size.micro,
  fontWeight: type.weight.semibold,
  letterSpacing: type.tracking.label,
  textTransform: "uppercase",
  color: neutral[500],
  lineHeight: 1.4,
};

// Numerals line up in columns everywhere in the product.
export const numeric = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: "'tnum' 1, 'cv05' 1",
};

// Shared dense-table recipe: sticky head, hairline rules, aligned numerals.
export const dataTable = {
  ...numeric,
  fontSize: type.size.data,
  borderCollapse: "separate",
  borderSpacing: 0,
  width: "100%",
  "& thead th": {
    position: "sticky",
    top: 0,
    zIndex: 2,
    bg: neutral[50],
    color: neutral[500],
    fontSize: type.size.micro,
    fontWeight: type.weight.semibold,
    letterSpacing: type.tracking.label,
    textTransform: "uppercase",
    textAlign: "left",
    whiteSpace: "nowrap",
    px: "10px",
    py: "8px",
    borderBottom: `1px solid ${neutral[200]}`,
  },
  "& tbody td": {
    px: "10px",
    py: "6px",
    color: neutral[700],
    borderBottom: `1px solid ${neutral[100]}`,
    whiteSpace: "nowrap",
  },
  // Numeric columns are right-aligned by convention.
  "& tbody td.num, & thead th.num": { textAlign: "right" },
  "& tbody tr:hover td": { bg: neutral[100] },
  "& tbody tr:last-of-type td": { borderBottom: "none" },
};

const tokens = {
  neutral,
  brand,
  signal,
  series,
  type,
  radius,
  elevation,
  layout,
  motion,
  surface,
  eyebrow,
  numeric,
  dataTable,
};

export default tokens;
